// core/storage.js — wrapper fino sobre localStorage.
//
// Objetivo: expor get/set/remove seguros com JSON automático e tratamento
// de QuotaExceeded/acesso negado (modo privado).
//
// Prefixo `eloq:` aplicado automaticamente a todas as chaves a partir da Fase 3.
// A migração das 13 chaves antigas `mbo_*` vive em core/migrations.js e roda
// uma vez no boot (main.js), antes do router.start().
//
// Uso:
//   import { storage } from './core/storage.js';
//   storage.set('alunos_cache', { timestamp: Date.now(), alunos: [...] });
//   const cache = storage.get('alunos_cache', null);   // 2º arg = default
//   storage.remove('alunos_cache');
//
//   // TTL opcional (empacotado como { v, exp }):
//   storage.setWithTTL('programacao', dados, 24*60*60*1000);
//   const prog = storage.getWithTTL('programacao');   // null se expirado/ausente
//
// Observações:
//  - As chaves passadas aqui são "lógicas" (sem prefixo) — o prefixo `eloq:`
//    é acrescentado internamente. Código consumidor nunca escreve o prefixo.

const PREFIX = 'eloq:';

function key(k) {
  return `${PREFIX}${k}`;
}

function isAvailable() {
  try {
    const probe = '__eloq_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function get(k, fallback = null) {
  try {
    const raw = localStorage.getItem(key(k));
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    console.warn(`[storage] get("${k}") falhou:`, e);
    return fallback;
  }
}

function set(k, value) {
  try {
    localStorage.setItem(key(k), JSON.stringify(value));
    return true;
  } catch (e) {
    // QuotaExceededError, acesso negado (modo privado Safari), etc.
    console.warn(`[storage] set("${k}") falhou:`, e);
    return false;
  }
}

function remove(k) {
  try {
    localStorage.removeItem(key(k));
    return true;
  } catch (e) {
    console.warn(`[storage] remove("${k}") falhou:`, e);
    return false;
  }
}

/**
 * Empacota valor com timestamp de expiração.
 * Formato: { v: <valor>, exp: <timestamp ms> }.
 */
function setWithTTL(k, value, ttlMs) {
  if (typeof ttlMs !== 'number' || ttlMs <= 0) {
    console.warn(`[storage] setWithTTL("${k}"): ttlMs inválido, usando set() normal.`);
    return set(k, value);
  }
  return set(k, { v: value, exp: Date.now() + ttlMs });
}

/**
 * Lê valor com TTL. Retorna null (ou fallback) se expirado ou ausente.
 * Se o valor não tiver envelope { v, exp } (dados legados), devolve como está.
 */
function getWithTTL(k, fallback = null) {
  const raw = get(k, null);
  if (raw === null) return fallback;
  if (typeof raw === 'object' && raw !== null && 'exp' in raw && 'v' in raw) {
    if (Date.now() > raw.exp) return fallback;
    return raw.v;
  }
  return raw; // legado / sem envelope
}

export const storage = {
  get,
  set,
  remove,
  setWithTTL,
  getWithTTL,
  isAvailable,
  _prefix: () => PREFIX, // util para testes e migração futura
};
